import { useCallback, useEffect, useMemo, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { formatDate } from 'sharedUi/dateUtils';
import { SectionLabel, StatusChip } from '../../components/common/panel';
import { PRETTY } from '../../components/common/ui-kit';
import { pipelinesService } from '../../services/pipelines.service';
import { knowledgeBaseService } from '../../services/knowledge-base.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PipelineSource, SourcePreview } from '../../types';
import { collectDocImages, type DocImage } from '../../utils/source-images';
import { sourceTone } from './pipeline-status';

/**
 * One source, in full. The list carries identity and status; everything that
 * explains WHAT a document actually is — its metadata, the pictures that came
 * with it, and the text that was indexed — lives here, behind one row click.
 *
 * Images are read from two places because they arrive from two: `images[]` on
 * the document, written when a fetch pulls the bytes in, and `metadata.images`,
 * which is what a catalogue import carries. Neither is authoritative on its own.
 */

const isUrl = (v: unknown) => typeof v === 'string' && /^https?:\/\//i.test(v);

/** Metadata is free-form, so it is rendered by SHAPE rather than by key. */
const MetaValue = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-ink-muted">—</span>;
  }
  if (typeof value === 'boolean') {
    return <StatusChip tone={value ? 'success' : 'neutral'} dot={false}>{value ? 'yes' : 'no'}</StatusChip>;
  }
  if (isUrl(value)) {
    return (
      <a href={value as string} target="_blank" rel="noreferrer noopener"
         className="break-all text-indigo-600 hover:underline dark:text-indigo-400">{value as string}</a>
    );
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-ink-muted">none</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (isUrl(v) ? (
          <a key={i} href={v as string} target="_blank" rel="noreferrer noopener"
             className="ui-tone ui-tone--info hover:underline">{String(v).split('/').pop() || String(v)}</a>
        ) : typeof v === 'object' && v ? (
          <span key={i} className="ui-tone ui-tone--neutral font-mono text-[11px]">{JSON.stringify(v)}</span>
        ) : (
          <span key={i} className="ui-tone ui-tone--neutral">{String(v)}</span>
        )))}
      </div>
    );
  }
  if (typeof value === 'object') {
    return (
      <pre className="ui-inset max-h-40 overflow-auto p-2 font-mono text-[11px] leading-relaxed">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <span className="break-words">{String(value)}</span>;
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 py-1.5 sm:grid-cols-[minmax(0,11rem)_1fr]">
    <dt className="font-mono text-xs text-ink-muted">{label}</dt>
    <dd className="min-w-0 text-sm text-gray-800 dark:text-gray-200">{children}</dd>
  </div>
);

/** An error from a `responseType: 'blob'` request carries its JSON body AS a Blob,
 *  which the shared extractor cannot read. */
async function blobError(e: any, fallback: string): Promise<string> {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text())?.message || fallback; } catch { return fallback; }
  }
  return extractErrorMessage(e, fallback);
}

const IMAGE_MIME = /^image\/(png|jpe?g|gif|webp|bmp|avif)$/;

/**
 * The document itself, rendered by what it is.
 *
 * The file is fetched WITH the session's token and handed to the page as a blob:
 * URL, and a blob: URL runs as this app's own origin. So only formats a browser
 * displays without executing are ever rendered: PDF in its own viewer, raster
 * images, and everything else as a TEXT NODE. HTML and SVG arrive from the backend
 * as text/plain and are shown as source — rendering one would let an uploaded file
 * run script with the user's session.
 */
const FileView = ({ source, load }: { source: PipelineSource; load: () => Promise<Blob> }) => {
  const [view, setView] = useState<{ url?: string; mime?: string; text?: string; error?: string } | null>(null);
  const ftype = (source.fileType || '').toUpperCase();
  const isWord = ftype === 'DOCX';

  useEffect(() => {
    if (source.sourceType === 'URL' || isWord) return;
    let live = true;
    let url: string | null = null;
    setView(null);
    load().then(async (blob) => {
      if (!live) return;
      const mime = (blob.type || '').split(';')[0].trim().toLowerCase();
      if (mime === 'application/pdf' || IMAGE_MIME.test(mime)) {
        url = URL.createObjectURL(blob);
        setView({ url, mime });
        return;
      }
      let text = await blob.text();
      if (ftype === 'JSON') { try { text = JSON.stringify(JSON.parse(text), null, 2); } catch { /* shown as-is */ } }
      setView({ text, mime });
    }).catch(async (e) => { if (live) setView({ error: await blobError(e, 'Could not load the file.') }); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [source._id, source.sourceType, isWord, ftype, load]);

  const download = async () => {
    try {
      const url = URL.createObjectURL(await load());
      const a = document.createElement('a');
      a.href = url; a.download = source.fileName || 'document';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setView({ error: await blobError(e, 'Could not download the file.') });
    }
  };

  if (source.sourceType === 'URL') {
    return (
      <div className="flex flex-col gap-2 text-sm text-gray-700 dark:text-gray-300" data-testid="SourceView.Url">
        <p>A web page is fetched each time a pipeline runs, so there is no stored copy to show here.</p>
        {source.url && <MetaValue value={source.url} />}
      </div>
    );
  }
  if (isWord) {
    return (
      <div className="flex flex-col items-start gap-3 text-sm text-gray-700 dark:text-gray-300" data-testid="SourceView.Word">
        <p>A browser cannot display a Word document. The <strong>Text</strong> tab shows exactly what was read
          from it — which is what gets chunked and searched.</p>
        <Button type="button" label="Download the .docx" icon="pi pi-download" size="small" severity="secondary"
                outlined onClick={download} />
      </div>
    );
  }
  if (!view) return <span className="ui-skel block h-[60vh] w-full !rounded-lg" aria-busy="true" />;
  if (view.error) return <Message severity="warn" text={view.error} className="w-full" />;
  if (view.url && view.mime === 'application/pdf') {
    return (
      <iframe src={view.url} title={source.fileName || 'Document'} data-testid="SourceView.Pdf"
              className="h-[68vh] w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700" />
    );
  }
  if (view.url) {
    return <img src={view.url} alt={source.title || source.fileName || ''} className="max-h-[68vh] max-w-full object-contain" />;
  }
  return (
    <pre data-testid="SourceView.Text"
         className="ui-inset max-h-[68vh] overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-[13px] leading-relaxed text-gray-800 dark:text-gray-200">
      {view.text || '(empty)'}
    </pre>
  );
};

/** What the pipeline reads, page by page — including the pages that read as nothing. */
const TextView = ({ preview }: { preview: SourcePreview }) => {
  const blank = preview.pages.filter((p) => !p.text.trim()).length;
  return (
    <div className="flex flex-col gap-3" data-testid="SourceView.Pages">
      <p className="text-xs text-ink-muted">
        {preview.pageCount.toLocaleString()} page{preview.pageCount === 1 ? '' : 's'} ·{' '}
        {preview.characterCount.toLocaleString()} characters extracted
        {preview.pages.length < preview.pageCount && ` · showing the first ${preview.pages.length}`}
      </p>
      {blank > 0 && (
        <Message severity="warn" className="w-full"
                 text={`${blank} page${blank === 1 ? ' has' : 's have'} no text layer — most likely scanned images. Nothing on ${blank === 1 ? 'it' : 'them'} can be searched unless the file is OCR'd first.`} />
      )}
      {preview.pages.map((pg, i) => (
        <div key={`${pg.page}-${i}`}>
          <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">
            <span>{pg.page != null ? `Page ${pg.page}` : `Part ${i + 1}`}</span>
            {pg.section && <span className="normal-case tracking-normal">· {pg.section}</span>}
            {!pg.text.trim() && <StatusChip tone="warn" dot={false}>no text</StatusChip>}
          </div>
          {pg.text.trim() && (
            <pre className="ui-inset whitespace-pre-wrap break-words p-3 text-sm leading-relaxed text-gray-800 dark:text-gray-200" style={PRETTY}>
              {pg.text}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
};

/** How THIS pipeline's current settings cut the document — before a run spends anything. */
const ChunksView = ({ preview }: { preview: SourcePreview }) => {
  const cfg = preview.chunkingConfig || {};
  const chunks = preview.chunks || [];
  return (
    <div className="flex flex-col gap-3" data-testid="SourceView.Chunks">
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <StatusChip tone="info" dot={false}>{cfg.strategy || 'RECURSIVE'}</StatusChip>
        <span className="ui-tone ui-tone--neutral">size <b className="tabular-nums">{cfg.chunkSize}</b></span>
        <span className="ui-tone ui-tone--neutral">overlap <b className="tabular-nums">{cfg.chunkOverlap}</b></span>
        <span className="ui-tone ui-tone--neutral">{cfg.scope === 'DOCUMENT' ? 'across pages' : 'within a page'}</span>
        {cfg.preset && <span className="ui-tone ui-tone--neutral">preset {cfg.preset}</span>}
        {cfg.boundaryPattern && <code className="ui-tone ui-tone--neutral">{cfg.boundaryPattern}</code>}
        <span className="ml-auto text-ink-muted tabular-nums">
          {(preview.chunkCount ?? chunks.length).toLocaleString()} chunks
          {chunks.length < (preview.chunkCount ?? 0) && ` · showing the first ${chunks.length}`}
        </span>
      </div>
      <p className="text-xs text-ink-muted">
        Change the strategy on the pipeline's Processing tab and open this again to compare — nothing here re-indexes anything.
      </p>
      {chunks.map((c) => (
        <div key={c.chunkIndex} className="ui-inset p-3">
          <div className="mb-1 flex flex-wrap items-center gap-x-2 text-[11px] tabular-nums text-ink-muted">
            <span className="font-mono text-indigo-600 dark:text-indigo-400">#{c.chunkIndex + 1}</span>
            {c.page != null && <span>p.{c.page}</span>}
            {c.section && <span>§ {c.section}</span>}
            <span>{c.content.length.toLocaleString()} chars</span>
          </div>
          <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800 dark:text-gray-200" style={PRETTY}>{c.content}</pre>
        </div>
      ))}
    </div>
  );
};

interface Props {
  /** Open the document through a pipeline… */
  pipelineId?: string;
  /** …or through the knowledge base that owns it. One of the two is required. */
  kbId?: string;
  sourceId: string | null;
  onHide: () => void;
}

const SourceViewDialog = ({ pipelineId, kbId, sourceId, onHide }: Props) => {
  const [source, setSource] = useState<PipelineSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [broken, setBroken] = useState<Record<string, true>>({});
  const [zoom, setZoom] = useState<DocImage | null>(null);
  const [tab, setTab] = useState(0);
  const [preview, setPreview] = useState<SourcePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const loadFile = useCallback(
    () => (kbId ? knowledgeBaseService.getDocumentFile(kbId, sourceId!) : pipelinesService.getSourceFile(pipelineId!, sourceId!)),
    [kbId, pipelineId, sourceId]);

  // Parsing a 100-page Act is real work, so the Text and Chunks tabs ask for it
  // when first opened, not every time the dialog is.
  const wantsPreview = tab === 1 || tab === 3;
  useEffect(() => {
    if (!sourceId || !wantsPreview || preview || previewError) return;
    let live = true;
    (kbId ? knowledgeBaseService.getDocumentPreview(kbId, sourceId) : pipelinesService.getSourcePreview(pipelineId!, sourceId))
      .then((p) => { if (live) setPreview(p); })
      .catch((e) => { if (live) setPreviewError(extractErrorMessage(e, 'Could not read this document.')); });
    return () => { live = false; };
  }, [sourceId, wantsPreview, preview, previewError, kbId, pipelineId]);

  useEffect(() => {
    if (!sourceId) { setSource(null); setError(null); return; }
    let live = true;
    setLoading(true); setError(null); setBroken({}); setZoom(null);
    setTab(0); setPreview(null); setPreviewError(null);
    (kbId ? knowledgeBaseService.getDocument(kbId, sourceId) : pipelinesService.getSource(pipelineId!, sourceId))
      .then((s) => { if (live) setSource(s); })
      .catch((e) => { if (live) setError(extractErrorMessage(e, 'Could not load this source')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [pipelineId, kbId, sourceId]);

  const images = useMemo(() => collectDocImages(source?.images, source?.metadata), [source]);
  const shown = images.filter((i) => !broken[i.src]);
  // `images` is rendered as a gallery above, so repeating the URLs as a metadata
  // row is noise; everything else the import carried is shown as it came.
  const meta = Object.entries(source?.metadata || {}).filter(([k]) => k !== 'images');

  return (
    <>
      <Dialog
        header={source?.title || source?.fileName || 'Source'}
        visible={!!sourceId} onHide={onHide} modal dismissableMask
        style={{ width: '1100px', maxWidth: '95vw' }}
        contentStyle={{ maxHeight: '82vh' }}
        data-testid="Pipeline.SourceView"
      >
        {loading && (
          <div className="flex flex-col gap-3" aria-busy="true">
            <span className="ui-skel h-4 w-48" />
            <span className="ui-skel h-24 w-full !rounded-lg" />
            <span className="ui-skel h-4 w-32" />
          </div>
        )}
        {error && <Message severity="error" text={error} className="w-full" />}

        {source && !loading && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusChip tone="info" dot={false}>{source.sourceType}</StatusChip>
              {source.fileType && <StatusChip tone="neutral" dot={false}>{source.fileType}</StatusChip>}
              <StatusChip tone={sourceTone(source.processingStatus)}>{source.processingStatus}</StatusChip>
              {source.version != null && (
                <span className="text-xs tabular-nums text-ink-muted">v{source.version}</span>
              )}
              {source.isCurrent === false && <StatusChip tone="warn" dot={false}>superseded</StatusChip>}
            </div>

            {source.validationError && <Message severity="warn" text={source.validationError} className="w-full" />}

            <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
              <TabPanel header="Document">
                <div className="flex flex-col gap-5">
            {shown.length > 0 && (
              <div>
                <SectionLabel icon="pi pi-images">
                  {shown.length === 1 ? 'Image' : `Images (${shown.length})`}
                </SectionLabel>
                <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {shown.map((img) => (
                    <button
                      key={img.src} type="button" onClick={() => setZoom(img)}
                      title={img.alt || img.caption || 'Open full size'}
                      className="ui-press ui-inset group flex flex-col overflow-hidden p-0 text-left"
                    >
                      <span className="flex aspect-square items-center justify-center overflow-hidden bg-white dark:bg-gray-900">
                        <img
                          src={img.src} alt={img.alt || ''} loading="lazy"
                          onError={() => setBroken((b) => ({ ...b, [img.src]: true }))}
                          className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
                        />
                      </span>
                      {(img.caption || img.alt) && (
                        <span className="truncate px-2 py-1 text-[11px] text-ink-muted">
                          {img.caption || img.alt}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {images.length > shown.length && (
                  <p className="mt-1.5 text-xs text-ink-muted">
                    {images.length - shown.length} image
                    {images.length - shown.length === 1 ? '' : 's'} could not be loaded from the origin.
                  </p>
                )}
              </div>
            )}

                  {source.sourceType === 'TEXT' ? (
                    <pre data-testid="SourceView.Text"
                         className="ui-inset max-h-[60vh] overflow-auto whitespace-pre-wrap break-words p-3 text-sm leading-relaxed text-gray-800 dark:text-gray-200"
                         style={PRETTY}>{source.textContent || '(empty)'}</pre>
                  ) : (
                    <FileView source={source} load={loadFile} />
                  )}
                </div>
              </TabPanel>
              <TabPanel header="Text">
                {previewError ? <Message severity="warn" text={previewError} className="w-full" />
                  : !preview ? <span className="ui-skel block h-64 w-full !rounded-lg" aria-busy="true" />
                  : <TextView preview={preview} />}
              </TabPanel>
              <TabPanel header="Details">
                <div className="flex flex-col gap-5">
            <div>
              <SectionLabel icon="pi pi-info-circle">Details</SectionLabel>
              <dl className="mt-1 divide-y divide-gray-100 dark:divide-gray-700/60">
                {source.fileName && <Row label="fileName">{source.fileName}</Row>}
                {source.description && <Row label="description"><MetaValue value={source.description} /></Row>}
                {source.url && <Row label="url"><MetaValue value={source.url} /></Row>}
                {source.canonicalUrl && <Row label="canonicalUrl"><MetaValue value={source.canonicalUrl} /></Row>}
                {source.siteName && <Row label="siteName">{source.siteName}</Row>}
                {source.author && <Row label="author">{source.author}</Row>}
                {source.publishedAt && <Row label="publishedAt">{formatDate(source.publishedAt)}</Row>}
                {source.fetchedAt && <Row label="fetchedAt">{formatDate(source.fetchedAt)}</Row>}
                {source.httpStatus != null && <Row label="httpStatus">{source.httpStatus}</Row>}
                {source.fileSize != null && (
                  <Row label="fileSize"><span className="tabular-nums">{source.fileSize.toLocaleString()} bytes</span></Row>
                )}
                {source.uploadedAt && <Row label="added">{formatDate(source.uploadedAt)}</Row>}
                {source.checksum && (
                  <Row label="checksum">
                    <span className="break-all font-mono text-xs">{source.checksum}</span>
                  </Row>
                )}
              </dl>
            </div>

            <div>
              <SectionLabel icon="pi pi-tags">
                Metadata{meta.length ? ` (${meta.length})` : ''}
              </SectionLabel>
              {meta.length ? (
                <dl className="mt-1 divide-y divide-gray-100 dark:divide-gray-700/60">
                  {meta.map(([k, v]) => <Row key={k} label={k}><MetaValue value={v} /></Row>)}
                </dl>
              ) : (
                <p className="mt-1 text-sm text-ink-muted">
                  This source carries no metadata. Metadata is copied onto every chunk built
                  from it, so it is what a retrieved answer can filter and cite by.
                </p>
              )}
            </div>

                </div>
              </TabPanel>
              {pipelineId && !kbId && (
                <TabPanel header="Chunks">
                  {previewError ? <Message severity="warn" text={previewError} className="w-full" />
                  : !preview ? <span className="ui-skel block h-64 w-full !rounded-lg" aria-busy="true" />
                    : <ChunksView preview={preview} />}
                </TabPanel>
              )}
            </TabView>
          </div>
        )}
      </Dialog>

      <Dialog visible={!!zoom} onHide={() => setZoom(null)} modal dismissableMask
              header={zoom?.caption || zoom?.alt || 'Image'} style={{ width: 'auto', maxWidth: '92vw' }}>
        {zoom && (
          <div className="flex flex-col items-center gap-2">
            <img src={zoom.src} alt={zoom.alt || ''} className="max-h-[70vh] max-w-full object-contain" />
            <a href={zoom.src} target="_blank" rel="noreferrer noopener"
               className="break-all text-xs text-indigo-600 hover:underline dark:text-indigo-400">{zoom.src}</a>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default SourceViewDialog;
